import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { Observable, of, tap, firstValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Token } from '../models/Token';
import { User } from '../models/User';
import { ScoresResponse } from '../models/ScoresResponse';
import { Role } from '../models/Role';
import { VerifyResponse } from '../models/VerifyResponse';
import { AffectedUsersResponse } from '../models/AffectedUsersResponse';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _user: User | null = null;
  private _lastFetchTime: number | null = null;
  private _isLoggingOut = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private storageService: StorageService,
  ) {}

  isLogged(): boolean {
    return this.storageService.getToken() !== null;
  }

  login(username: string, password: string): Observable<Token> {
    const url = `/api/v1/login/access-token`;
    const data: any = {
      username: username,
      password: password,
    };

    const body = new URLSearchParams();
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        body.set(key, data[key]);
      }
    }

    return this.http.post<Token>(url, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  }

  anonymousLogin(): Observable<Token> {
    const url = `/api/v1/login/anonymous`;

    return this.http.post<Token>(url, {});
  }

  deleteMe(): Observable<any> {
    const url = `/api/v1/users/me`;

    return this.http.delete(url);
  }

  passwordRecovery(email: string): Observable<any> {
    const url = `/api/v1/password-recovery`;
    const data: any = {
      email: email,
    };

    return this.http.post(url, data);
  }

  resetPassword(token: string, password: string): Observable<any> {
    const url = `/api/v1/users/password`;
    const data: any = {
      token: token,
      new_password: password,
    };

    return this.http.patch(url, data);
  }

  signup(email: string, password: string, firstname: string, lastname: string) {
    const url = `/api/v1/users/?send_email=false`;
    const data: any = {
      email: email,
      password: password,
      first_name: firstname,
      last_name: lastname,
      full_name: `${firstname} ${lastname}`,
      is_parent: true,
      // pseudo: this.genererStringAleatoire(10),
    };

    return this.http.post<User>(url, data, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  anonymousSignup(email: string, password: string, firstname: string, lastname: string) {
    const url = `/api/v1/register-anonymous-user`;
    const data: any = {
      email: email,
      password: password,
      first_name: firstname,
      last_name: lastname,
      full_name: `${firstname} ${lastname}`,
      is_parent: true,
      // pseudo: this.genererStringAleatoire(10),
    };

    return this.http.post<User>(url, data, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  genererStringAleatoire(longueur: number): string {
    const caracteres = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let resultat = '';
    for (let i = 0; i < longueur; i++) {
      const indexAleatoire = Math.floor(Math.random() * caracteres.length);
      resultat += caracteres.charAt(indexAleatoire);
    }
    return resultat;
  }

  verifyToken(token: string): Observable<VerifyResponse> {
    const url = `/api/v1/users/verify_token?token=${token}`;

    return this.http.post<VerifyResponse>(url, {});
  }

  apiLogout(): Observable<any> {
    const url = `/api/v1/logout`;

    return this.http.post<any>(url, {});
  }

  async logout(mode: 'manual' | 'forced' = 'manual') {
    if (this._isLoggingOut) {
      console.warn('[LOGOUT] Logout already in progress, skipping duplicate call');
      return;
    }

    this._isLoggingOut = true;
    console.log(`[LOGOUT] Start (${mode})`);
    const cachedGarIdo = this._getCachedGarIdo();

    try {
      await firstValueFrom(this.apiLogout());
      console.log('[LOGOUT] API logout call succeeded');
    } catch (error) {
      console.error('[LOGOUT] API logout call failed:', error);
      // Continue local cleanup and redirection even if backend call fails.
    }

    setTimeout(() => {
      const garRedirectUrl = (window as any)['env']?.['garRedirectUrl'];
      this.storageService.clearAllLocalStorage();
      this._clearUserCache();

      if (cachedGarIdo !== null && cachedGarIdo !== undefined) {
        if (garRedirectUrl) {
          window.location.href = garRedirectUrl;
        } else {
          this.router.navigate(['/login']);
        }
      } else if (this.storageService.getMobile()) {
        window.location.href = 'bedici://logout';
      } else {
        this.router.navigate(['/login']);
      }

      this._isLoggingOut = false;
    }, 300);
  }

  private getMeInfo(): Observable<User> {
    const url = `/api/v1/users/me`;

    return this.http.get<User>(url);
  }

  getMyScores(role: Role): Observable<ScoresResponse> {
    const url = `/api/v1/users/scores/me?client_enum=${role}`;

    return this.http.get<ScoresResponse>(url);
  }

  getAffectedUsers(): Observable<AffectedUsersResponse> {
    const url = `/api/v1/users/me/affected_users`;

    return this.http.get<AffectedUsersResponse>(url);
  }

  postAffectedUser(role: Role, pseudo: string, firstname: string, lastname: string, password: string): Observable<AffectedUsersResponse> {
    const url = `/api/v1/users/affect_users`;
    let body: any = {
      pseudo: pseudo,
      first_name: firstname,
      last_name: lastname,
      password: password,
      is_parent: role === Role.parent,
    };

    return this.http.post<AffectedUsersResponse>(url, [body]);
  }

  updateMyUser(userId: string, graphism?: string, avatar?: number, firstConnection?: boolean): Observable<User> {
    const url = `/api/v1/users/me`;
    let body: any = {
      user_id: userId,
    };

    if (graphism) {
      body['graphism'] = graphism;
    }

    if (avatar !== undefined) {
      body['avatar_id'] = `${avatar}`;
    }

    if (firstConnection !== undefined) {
      body['is_first_connection'] = firstConnection;
    }

    return this.http.patch<User>(url, body);
  }

  getUserInfoOrRefreshIfNeeded(): Observable<User> {
    if (!this._user || this._shouldRefresh()) {
      return this.getMeInfo().pipe(tap((user: User) => this._updateCache(user)));
    } else {
      return of(this._user);
    }
  }

  forceUpdateUser(): Observable<User> {
    return this.getMeInfo().pipe(tap((user: User) => this._updateCache(user)));
  }

  ensureSession(): Observable<boolean> {
    if (this.isLogged()) {
      return of(true);
    }

    return this.forceUpdateUser().pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  private _shouldRefresh(): boolean {
    if (!this._lastFetchTime) return true;

    const timeElapsed = (Date.now() - this._lastFetchTime) / 1000 / 60; // minutes

    return timeElapsed > 5;
  }

  private _updateCache(user: User): void {
    this._user = user;
    this._lastFetchTime = Date.now();
    this.storageService.setGarIdo(user.gar_ido);
  }

  private _clearUserCache(): void {
    this._user = null;
    this._lastFetchTime = null;
  }

  private _getCachedGarIdo(): string | null {
    return this.storageService.getGarIdo();
  }
}
